import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Banner } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { nextPlanUp } from '../plans';

interface Usage {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
  planKey?: string;
}

/** Fraction of the monthly limit at which the warning appears. */
const WARN_AT = 0.8;

/**
 * Shown on every merchant page once a store has used 80% of its monthly
 * signing requests, and in red once it has none left (automatic sends are
 * then skipped silently, so the merchant must not miss this).
 */
export default function UsageBanner() {
  const { request, startSubscription } = useApi();
  const { pathname } = useLocation();
  const [usage, setUsage] = useState<Usage | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    request<{ status: string; usage?: Usage }>('/auth/billing-status')
      .then((b) => setUsage(b.status === 'ACTIVE' && b.usage ? b.usage : null))
      .catch(() => {});
  }, [request, pathname]);

  const next = nextPlanUp(usage?.planKey);

  const upgrade = useCallback(async () => {
    if (!next) return;
    setUpgrading(true);
    try {
      await startSubscription(next.key);
    } catch {
      setUpgrading(false);
    }
  }, [startSubscription, next]);

  if (!usage || usage.used < usage.limit * WARN_AT) return null;

  const atLimit = usage.remaining <= 0;
  if (dismissed && !atLimit) return null;

  const resets = new Date(usage.resetsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });

  const action = next
    ? { content: `Upgrade to ${next.name} — ${next.monthlyLimit}/month`, onAction: upgrade, loading: upgrading }
    : { content: 'Contact support', url: 'mailto:support@merchantkits.com', external: true };

  return (
    <div style={{ maxWidth: 1000, margin: '16px auto 0', padding: '0 16px' }}>
      {atLimit ? (
        <Banner tone="critical" title={`You've used all ${usage.limit} signing requests this month`} action={action}>
          <p>
            New orders won't receive contracts until {resets}, and manual requests can't be sent.
            {next ? ` Upgrade to ${next.name} to send up to ${next.monthlyLimit} a month — it applies as soon as you approve it.` : ''}
          </p>
        </Banner>
      ) : (
        <Banner
          tone="warning"
          title={`You've used ${usage.used} of your ${usage.limit} signing requests this month`}
          action={action}
          onDismiss={() => setDismissed(true)}
        >
          <p>
            Once you reach {usage.limit}, new orders won't receive contracts until {resets}.
          </p>
        </Banner>
      )}
    </div>
  );
}
